/**
 * PlannedExpensesList Component
 *
 * Configured wrapper around DynamicEntryList for one-time future expenses.
 * Each row has: description (text), amount (currency), year (year).
 */

import React from "react";
import { DynamicEntryList, type DynamicEntryField } from "./DynamicEntryList";
import type { PlannedExpense } from "../types/settings";

/** Field definitions for planned expense entries */
const PLANNED_EXPENSE_FIELDS: DynamicEntryField[] = [
  {
    name: "description",
    type: "text",
    placeholder: "Description",
  },
  {
    name: "amount",
    type: "currency",
    placeholder: "0",
  },
  {
    name: "year",
    type: "year",
    placeholder: "Year",
  },
];

export interface PlannedExpensesListProps {
  /** Array of planned expense entries */
  expenses: PlannedExpense[];
  /** Called when user clicks "Add" */
  onAdd: () => void;
  /** Called with entry index when user clicks "Remove" */
  onRemove: (index: number) => void;
  /** Called with (index, fieldName, value) when a field changes */
  onChange: (index: number, field: string, value: unknown) => void;
  /** Optional additional class names */
  className?: string;
}

/**
 * PlannedExpensesList — dynamic list for one-time future expenses
 *
 * Pre-configured with description/amount/year fields and max 10 entries.
 * Used in the Expenses form's advanced section.
 */
export const PlannedExpensesList: React.FC<PlannedExpensesListProps> = ({
  expenses,
  onAdd,
  onRemove,
  onChange,
  className,
}) => {
  return (
    <DynamicEntryList
      id="planned-expenses-list"
      className={className}
      entries={expenses as unknown as Record<string, unknown>[]}
      fields={PLANNED_EXPENSE_FIELDS}
      onAdd={onAdd}
      onRemove={onRemove}
      onChange={onChange}
      addLabel="Add planned expense"
      emptyLabel="No planned expenses added yet"
      maxEntries={10}
    />
  );
};

export default PlannedExpensesList;
